import { getMomentTz, type MenuOptionType } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import type { CellStyle, ColDef } from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { RECLASSIFIED_COLOR } from "@/shared/colors";
import {
	buildBundleColumns,
	buildCheckActionsColumn,
	buildPositionColumns,
} from "../../components/last-checks-table/hooks/useTableData";
import type { SawQualityCheckRow } from "../../repositories/sawQualityChecksReportRepository";

const moment = getMomentTz();

const fmtLen = (v: number | null | undefined) =>
	v == null ? "" : String(Number(v));

type Props = {
	onReclassify: (check: SawQualityCheckRow) => void;
	onEdit: (check: SawQualityCheckRow) => void;
	onDelete: (check: SawQualityCheckRow) => void;
};

export const useTableData = ({ onReclassify, onEdit, onDelete }: Props) => {
	const columnDefs = useMemo((): ColDef<SawQualityCheckRow>[] => {
		const [bundlePieces, bundleStart, bundleEnd] =
			buildBundleColumns<SawQualityCheckRow>();
		return [
			{
				field: "CheckTime",
				headerName: "Time",
				minWidth: 145,
				flex: 1.4,
				valueFormatter: (params) =>
					params.value ? moment(params.value).format("HH:mm - DD/MM/YY") : "",
			},
			{
				field: "AssetName",
				headerName: "Line",
				minWidth: 130,
				flex: 1.2,
				valueFormatter: (params) => params.value ?? "",
			},
			{
				field: "ProductionDate",
				headerName: "Shift",
				minWidth: 110,
				flex: 1,
				valueGetter: (params) => {
					const d = params.data;
					if (!d || d.ProductionDate == null) return "";
					const shift = [d.Shift, d.Crew].filter(Boolean).join("-");
					return `${moment(d.ProductionDate).format("DD/MM")} ${shift}`.trim();
				},
			},
			bundlePieces,
			{
				field: "PartNumber",
				headerName: "Part",
				minWidth: 140,
				flex: 1.4,
				valueGetter: (params) => params.data?.PartNumber ?? "",
				tooltipValueGetter: (params) =>
					params.data?.IsReclassified
						? "Reclassified — this is the part the bundle was changed to"
						: "",
				cellStyle: (params): CellStyle =>
					params.data?.IsReclassified
						? { color: RECLASSIFIED_COLOR, fontWeight: 600 }
						: {},
			},
			bundleStart,
			bundleEnd,
			...buildPositionColumns<SawQualityCheckRow>(),
			{
				colId: "minMax",
				headerName: "Min/Max",
				minWidth: 120,
				flex: 1.1,
				sortable: false,
				valueGetter: (params) => {
					const d = params.data;
					if (!d || d.MinLength == null || d.MaxLength == null) return "";
					return `${fmtLen(d.MinLength)} / ${fmtLen(d.MaxLength)}`;
				},
			},
			{
				field: "TargetLength",
				headerName: "Target",
				minWidth: 100,
				flex: 1,
				valueGetter: (params) => fmtLen(params.data?.TargetLength),
			},
			{
				field: "CreatedBy",
				headerName: "By",
				minWidth: 90,
				flex: 1,
				valueFormatter: (params) => params.value ?? "",
			},
			buildCheckActionsColumn({ onReclassify, onEdit, onDelete }),
		];
	}, [onReclassify, onEdit, onDelete]);

	// First option is bold and mirrors the row double-click.
	const getMenuOptions = useCallback(
		(check: SawQualityCheckRow) => {
			const options: MenuOptionType[] = [
				{
					name: "Edit Quality Check",
					key: "edit_quality_check",
					onClick: () => onEdit(check),
					icon: <EditIcon />,
					style: { fontWeight: "bold" },
				},
			];
			if (check?.BundleId != null) {
				options.push({
					name: "Reclassify Bundle",
					key: "reclassify_bundle",
					onClick: () => onReclassify(check),
					icon: <SwapHorizIcon sx={{ color: RECLASSIFIED_COLOR }} />,
				});
			}
			options.push({
				name: "Delete Quality Check",
				key: "delete_quality_check",
				onClick: () => onDelete(check),
				icon: <DeleteIcon />,
			});
			return options;
		},
		[onReclassify, onEdit, onDelete],
	);

	return { columnDefs, getMenuOptions };
};
